import { useCallback, useEffect, useState } from "react";
import { poolApi } from "../../services/businessApi";
import { vocabularyFor, weightLabel } from "../../services/productVocabulary";

/**
 * The last few things that happened to one product's pool (#495).
 *
 * Asked 6 Oct: "All 3 pools also need a short visible history in a
 * scrolling pane."
 *
 * The Honey Pool has its own ledger screen; the other-products cards
 * showed a balance and nothing else, so an adjustment landed with no
 * confirmation it had and no way to see what the number was made of.
 *
 * ── SHORT, AND ON THE CARD ──
 *
 * Not a page. Honey has a ledger of its own because honey has volume --
 * a season of extractions, bottling runs and sales. A wax pool has a
 * handful of entries, and sending somebody to another screen to read six
 * rows is worse than showing them here.
 *
 * ── IT ASKS FOR ITS OWN PRODUCT ──
 *
 * `poolApi.movements(productType)`. The endpoint had NO product filter
 * until this change, which meant the honey ledger had been showing wax
 * and pollen adjustments since V120 gave movements a product -- a
 * history that did not add up to the balance printed above it. Rendering
 * four products' movements under one card's heading would have been the
 * same fault from the other direction.
 *
 * ── IT LOADS WHEN OPENED, NOT WITH THE PAGE ──
 *
 * Four products on screen would be four extra requests on every visit to
 * a page whose main job is showing balances. The pool page already
 * fetches per-product data; this is the detail somebody asks for, so it
 * is fetched when they ask.
 */
export function ProductPoolHistory({ productType, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const vocab = vocabularyFor(productType);

  const load = useCallback(() => {
    setFailed(false);
    poolApi
      .movements(productType)
      /* Only this product's, even though the server now filters: a stale
         build of the API would return everything, and a card that
         silently showed another product's history would be the bug this
         exists to fix wearing a different hat. */
      .then((r) => setRows((r.movements || []).filter(
        (m) => !m.product_type || m.product_type === productType)))
      /* null stays null on failure and `failed` carries the reason.
         An empty array here would say "nothing has ever happened to this
         pool", which is a claim -- and a false one when the request
         simply did not arrive. */
      .catch(() => setFailed(true));
  }, [productType]);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  return (
    <div style={{ marginTop: "var(--space-3)" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="link-button"
      >
        {open ? "Hide recent activity" : "Recent activity"}
      </button>

      {open && (
        <>
          {failed && (
            <p style={{ margin: 0, color: "var(--color-danger)",
                        fontSize: "var(--font-size-sm)" }}>
              Could not load the history.{" "}
              <button
                type="button" onClick={load}
                style={{ background: "none", border: "none", padding: 0,
                         font: "inherit", textDecoration: "underline",
                         cursor: "pointer", color: "inherit" }}
              >
                Try again
              </button>
            </p>
          )}

          {!failed && rows === null && (
            <p style={{ margin: 0, color: "var(--color-text-muted)",
                        fontSize: "var(--font-size-sm)" }}>
              Loading…
            </p>
          )}

          {!failed && rows !== null && rows.length === 0 && (
            <p style={{ margin: 0, color: "var(--color-text-muted)",
                        fontSize: "var(--font-size-sm)" }}>
              Nothing recorded against this pool yet.
            </p>
          )}

          {!failed && rows !== null && rows.length > 0 && (
            /* SCROLLS rather than growing, as asked. A card that gets
               taller with every adjustment pushes the other products off
               the screen. */
            <ul style={{
              listStyle: "none", margin: 0, padding: 0,
              maxHeight: 180, overflowY: "auto",
              borderTop: "1px solid var(--color-border)",
            }}>
              {rows.map((m) => (
                <li
                  key={m.movement_id}
                  style={{
                    display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
                    alignItems: "baseline",
                    padding: "var(--space-2) 0",
                    borderBottom: "1px solid var(--color-border)",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  <span style={{ color: "var(--color-text-muted)", minWidth: "7em" }}>
                    {new Date(m.created_date).toLocaleDateString()}
                  </span>
                  <strong>{m.movement_type}</strong>
                  {/* SIGNED, because in and out is the whole question a
                      history answers. weightLabel reads it back in the
                      product's own unit -- a 4 oz pollen jar shown as
                      0.25 lb hands back a different number than the one
                      typed in. */}
                  <span>
                    {Number(m.pounds_delta) > 0 ? "+" : "−"}
                    {weightLabel(productType, Math.abs(Number(m.pounds_delta)))}
                  </span>
                  {m.bottle_count ? (
                    <span style={{ color: "var(--color-text-muted)" }}>
                      {m.bottle_count} × {m.bottle_size_label || vocab.unit}
                    </span>
                  ) : null}
                  {m.reason ? (
                    <span style={{ color: "var(--color-text-muted)" }}>{m.reason}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
